
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MentoriaOffering } from "@/hooks/useMentoriasAdmin";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface MentoriaDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  mentoria: MentoriaOffering | null;
}

export const MentoriaDetailsDialog: React.FC<MentoriaDetailsDialogProps> = ({
  isOpen,
  onClose,
  mentoria
}) => {
  if (!mentoria) return null;

  const formatPrice = (priceCents: number) => {
    if (priceCents === 0) {
      return 'Gratuito';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(priceCents / 100);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd 'de' MMMM 'de' yyyy, HH:mm", { locale: ptBR });
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent 
        className="bg-[#181D24] text-white border-[#222A35] max-w-2xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-white">{mentoria.title}</DialogTitle>
          <DialogDescription className="text-gray-400">
            Detalhes da oferta de mentoria
          </DialogDescription>
        </DialogHeader>
        
        {mentoria.cover_image_url && (
          <div className="mt-2">
            <img 
              src={mentoria.cover_image_url} 
              alt={mentoria.title}
              className="w-full h-48 object-cover rounded-lg"
            />
          </div>
        )}
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-[#00B6F0]">Informações básicas</h3>
            <p><span className="text-gray-400">Mentor:</span> {mentoria.mentor?.profile?.nickname}</p>
            <p><span className="text-gray-400">Jogo:</span> {mentoria.game?.name}</p>
            <p><span className="text-gray-400">Preço:</span> {formatPrice(mentoria.price_cents)}</p>
            <p><span className="text-gray-400">Duração:</span> {mentoria.duration_minutes} minutos</p>
            <p><span className="text-gray-400">Status:</span> {mentoria.is_active ? "Ativo" : "Inativo"}</p>
          </div>
          
          <div className="space-y-2">
            <h3 className="font-semibold text-[#00B6F0]">Detalhes adicionais</h3>
            <p><span className="text-gray-400">Tipo:</span> {mentoria.is_group ? "Em grupo" : "Individual"}</p>
            {mentoria.is_group && (
              <p><span className="text-gray-400">Participantes máx:</span> {mentoria.max_participants}</p>
            )}
            <p><span className="text-gray-400">Criado em:</span> {formatDate(mentoria.created_at)}</p>
          </div>
        </div>
        
        <div className="mt-2">
          <h3 className="font-semibold text-[#00B6F0] mb-2">Descrição</h3>
          <div className="bg-[#151A23] p-3 rounded-md text-gray-200">
            {mentoria.description}
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="mt-2 border-[#222A35] text-gray-200 hover:bg-[#222A35]"
          >
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
